import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Editor,
  EditorState,
  RichUtils,
  convertToRaw,
  convertFromHTML,
  ContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import { List } from "lucide-react";

const styleMap = {
  BOLD: { fontWeight: 700 },
  ITALIC: { fontStyle: "italic" },
  UNDERLINE: { textDecoration: "underline" },
};

function htmlToEditorState(html) {
  if (!html) return EditorState.createEmpty();
  const blocksFromHTML = convertFromHTML(html.replace(/\u200F/g, ""));
  const content = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );
  return EditorState.createWithContent(content);
}

const blockTypes = [{ label: "list", style: "unordered-list-item" }];

const RichTextEditor = ({
  value,
  onChange,
  toolbarRef,
  label,
  labelClassName,
  isRTL = false,
}) => {
  const [editorState, setEditorState] = useState(() => {
    return htmlToEditorState(value);
  });

  const lastValueRef = useRef(value);
  const isInternalChange = useRef(false);

  // Memoize the onChange callback to prevent unnecessary re-renders
  const handleChangeCallback = useCallback(
    (html) => {
      isInternalChange.current = true;
      onChange(html);
      // Reset the flag after a short delay
      setTimeout(() => {
        isInternalChange.current = false;
      }, 0);
    },
    [onChange]
  );

  useEffect(() => {
    // Only update if the value actually changed and it's not from our internal change
    if (value !== lastValueRef.current && !isInternalChange.current) {
      setEditorState(htmlToEditorState(value));
      lastValueRef.current = value;
    }
  }, [value]);

  const handleChange = (state) => {
    setEditorState(state);

    const raw = convertToRaw(state.getCurrentContent());
    const html = draftToHtml(raw).replace(/\u200F/g, "");

    handleChangeCallback(html);
  };

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const toggleInlineStyle = (style) => {
    handleChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType) => {
    handleChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const currentBlockType = editorState
    .getCurrentContent()
    .getBlockForKey(editorState.getSelection().getStartKey())
    .getType();

  const currentStyle = editorState.getCurrentInlineStyle();

  // Simple inline styles to avoid CSS conflicts
  const editorBoxStyle = {
    minHeight: "72px",
    maxHeight: "120px",
    border: "1px solid #dee2e6",
    borderRadius: "8px",
    padding: "6px 8px",
    fontSize: "14px",
    color: "#5d6c7d",
    fontWeight: 400,
    backgroundColor: "#fff",
    overflowY: "auto",
    direction: isRTL ? "rtl" : "ltr",
    textAlign: isRTL ? "right" : "left",
  };

  const toolbarStyle = {
    display: "flex",
    gap: "4px",
    marginBottom: "8px",
    alignItems: "center",
    height: "28px",
  };

  const buttonStyle = {
    background: "none",
    border: "none",
    color: "#5d6c7d",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    borderRadius: "4px",
    padding: "2px 4px",
    height: "18px",
    minWidth: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <label className={labelClassName}>{label}</label>
        <div style={toolbarStyle} ref={toolbarRef}>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleInlineStyle("BOLD");
            }}
            style={{
              ...buttonStyle,
              backgroundColor: currentStyle.has("BOLD")
                ? "rgb(210 232 255)"
                : "transparent",
            }}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleInlineStyle("ITALIC");
            }}
            style={{
              ...buttonStyle,
              backgroundColor: currentStyle.has("ITALIC")
                ? "rgb(210 232 255)"
                : "transparent",
            }}
          >
            <i>I</i>
          </button>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleInlineStyle("UNDERLINE");
            }}
            style={{
              ...buttonStyle,
              backgroundColor: currentStyle.has("UNDERLINE")
                ? "rgb(210 232 255)"
                : "transparent",
            }}
          >
            <u>U</u>
          </button>
          {blockTypes.map((block) => (
            <button
              key={block.style}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                toggleBlockType(block.style);
              }}
              style={{
                ...buttonStyle,
                backgroundColor:
                  currentBlockType === block.style ? "rgb(210 232 255)" : "transparent",
              }}
            >
              {block.label === "list" ? <List size={16} /> : block.label}
            </button>
          ))}
        </div>
      </div>

      <div style={editorBoxStyle}>
        <Editor
          editorState={editorState}
          onChange={handleChange}
          handleKeyCommand={handleKeyCommand}
          customStyleMap={styleMap}
          spellCheck={true}
          // placeholder="Type here..."
        />
      </div>

      {/* Debug info */}
      {/* <div style={{ marginTop: '5px', fontSize: '11px', color: '#999' }}>
        Debug: Cursor at {editorState.getSelection().getAnchorOffset()}, 
        Content: "{editorState.getCurrentContent().getPlainText().substring(0, 20)}..."
      </div> */}
    </div>
  );
};

export default RichTextEditor;
